import React, { useState, useEffect } from 'react';
import './TransactionDetails.css';

const TransactionDetails = ({ transactionHash, onClose }) => {
  const [transactionData, setTransactionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch detailed transaction information
  const fetchTransactionDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      // First, get ALL transactions to find this one
      const response = await fetch('http://localhost:9545', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          jsonrpc: '2.0',
          method: 'avo_getRecentTransactions',
          params: [10000], // Get up to 10,000 transactions to find any transaction
          id: 1
        })
      });

      const result = await response.json();
      if (result.result && result.result.transactions) {
        const transaction = result.result.transactions.find(tx => tx.hash === transactionHash);
        
        if (transaction) {
          setTransactionData(transaction);
        } else {
          setError('Transaction not found');
        }
      } else {
        setError('Failed to fetch transaction data');
      }
    } catch (err) {
      setError('Failed to load transaction details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (transactionHash) {
      fetchTransactionDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionHash]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      // Copied to clipboard successfully
    });
  };

  if (loading) {
    return (
      <div className="transaction-details-overlay">
        <div className="transaction-details-modal">
          <div className="transaction-details-header">
            <h2>Transaction Details</h2>
            <button className="close-button" onClick={onClose}>×</button>
          </div>
          <div className="loading-content">
            <p>Loading transaction details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="transaction-details-overlay">
        <div className="transaction-details-modal">
          <div className="transaction-details-header">
            <h2>Transaction Details</h2>
            <button className="close-button" onClick={onClose}>×</button>
          </div>
          <div className="error-content">
            <p>Error: {error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="transaction-details-overlay">
      <div className="transaction-details-modal">
        <div className="transaction-details-header">
          <h2>Transaction Details</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        
        <div className="transaction-details-content">
          <div className="detail-section">
            <h3>Basic Information</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <span className="detail-label">Transaction Hash:</span>
                <span className="detail-value hash-value">
                  {transactionData.hash}
                  <button 
                    className="copy-button" 
                    onClick={() => copyToClipboard(transactionData.hash)}
                    title="Copy to clipboard"
                  >
                    📋
                  </button>
                </span>
              </div>
              
              <div className="detail-item">
                <span className="detail-label">Status:</span>
                <span className={`detail-value status-${transactionData.status}`}>
                  {transactionData.status === 'success' ? '✅ Success' : '❌ Failed'}
                </span>
              </div>
              
              <div className="detail-item">
                <span className="detail-label">Block Number:</span>
                <span className="detail-value">#{transactionData.blockNumber}</span>
              </div>
              
              <div className="detail-item">
                <span className="detail-label">Timestamp:</span>
                <span className="detail-value">{transactionData.timeFormatted}</span>
              </div>
            </div>
          </div>

          <div className="detail-section">
            <h3>Addresses</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <span className="detail-label">From:</span>
                <span className="detail-value address-value">
                  {transactionData.from}
                  <button 
                    className="copy-button" 
                    onClick={() => copyToClipboard(transactionData.from)}
                    title="Copy to clipboard"
                  >
                    📋
                  </button>
                </span>
              </div>
              
              <div className="detail-item">
                <span className="detail-label">To:</span>
                <span className="detail-value address-value">
                  {transactionData.to}
                  <button 
                    className="copy-button" 
                    onClick={() => copyToClipboard(transactionData.to)}
                    title="Copy to clipboard"
                  >
                    📋
                  </button>
                </span>
              </div>
            </div>
          </div>

          <div className="detail-section">
            <h3>Value & Fees</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <span className="detail-label">Amount:</span>
                <span className="detail-value amount-value">
                  {transactionData.valueInAVO} AVO
                </span>
              </div>
              
              <div className="detail-item">
                <span className="detail-label">Amount (Wei):</span>
                <span className="detail-value wei-value">
                  {transactionData.value} wei
                </span>
              </div>
              
              <div className="detail-item">
                <span className="detail-label">Gas Fee:</span>
                <span className="detail-value fee-value">
                  {transactionData.gasFeeInAVO} AVO
                </span>
              </div>
              
              <div className="detail-item">
                <span className="detail-label">Gas Used:</span>
                <span className="detail-value gas-value">
                  {transactionData.gasUsed.toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          <div className="detail-section">
            <h3>🔥 Gas Fee Distribution (100% DEFLATIONARY)</h3>
            <div className="fee-distribution">
              <div className="fee-item burn-highlight">
                <span className="fee-label">🔥 BURNED to DEAD Address (100%):</span>
                <span className="fee-value burn-value">{parseFloat(transactionData.gasFeeInAVO).toFixed(9)} AVO</span>
              </div>
              <div className="fee-item dead-address">
                <span className="fee-label">Burn Address:</span>
                <span className="fee-value address-value">
                  0x000000000000000000000000000000000000DEAD
                  <button 
                    className="copy-button" 
                    onClick={() => copyToClipboard('0x000000000000000000000000000000000000DEAD')}
                    title="Copy to clipboard"
                  >
                    📋
                  </button>
                </span>
              </div>
              <div className="fee-info">
                <p className="deflationary-note">
                  ✅ <strong>Completely Deflationary Protocol</strong> - All gas fees are permanently burned, reducing total supply with every transaction.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransactionDetails;